/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useState } from 'react'
import { useMedia } from 'react-use'

import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../../shared/modal'
import {
  BoardGroupFragment,
  PlanItemDetailsFragment,
  CatalogSearchResultFragment,
  usePlaceholderCourseDetailLazyQuery,
  AlertType
} from '../../../../generated/graphql'
import { SecondaryButton, PrimaryButton } from '../../../shared/buttons'
import { ButtonType } from '../../../shared/buttons/types'
import { ModalSize } from '../../../shared/modal/Modal/Modal'
import CourseForm from '../CourseForm'
import NonCourseForm from '../NonCourseForm'
import { BP_SMALL_UP } from '../../../../lib/styles/breakpoints'
import { userIsOnlyStudent } from '../../../../lib/authenticationUtils'
import { DetailsPlanAlert } from '../../../../types'
import useAppSettings from '../../../../lib/hooks/useAppSettings'
import { stringFormat } from '../../../../lib/utils'
import { getPlanItemAlerts } from './utils'
import { useAuth } from '../../../provider/OIDCProvider'

export type PlanItemState = {
  creditsMax: number | undefined
  creditsMin: number | undefined
  description: string | undefined
  group: BoardGroupFragment | null | undefined
  inProgress?: boolean | null
  isCoursePlaceholder: boolean
  locked: boolean
  nonCourseCreditsMax: number | undefined
  nonCourseCreditsMin: number | undefined
  nonCourseDescription: string | undefined
  nonCourseTitle: string
  required: boolean
  requirementTitles: string[]
  selectedCourse: CatalogSearchResultFragment | undefined
  selectedCoursePlaceholders: CatalogSearchResultFragment[] | undefined
  title: string
  usedToMeetRequirement?: boolean | null
}

export type PlanItemDetailsModalProps = {
  /** List of alerts associated the item ready to display */
  alerts: Array<DetailsPlanAlert>
  /** Campus used to filter courses */
  campus?: string
  /** Whether the delete button should be disabled */
  disableDelete?: boolean
  /** Whether the group field should be disabled */
  disableGroupChange?: boolean
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when user triggers a close event (eg. button click or ESC key) */
  onCancel: () => void
  /** Event callback when user clicks the Delete button */
  onDelete: () => void
  /** Event callback when user clicks the Save button */
  onSave: (planItemState: PlanItemState) => void
  /** List of available groups for current plan */
  planGroups: Array<BoardGroupFragment>
  /** Current Plan Item */
  planItem: PlanItemDetailsFragment
  /** Whether all of the inputs and buttons should be disabled */
  readOnly: boolean
}

const defaultProps: Pick<
  PlanItemDetailsModalProps,
  'campus' | 'disableDelete' | 'disableGroupChange'
> = {
  campus: undefined,
  disableDelete: false,
  disableGroupChange: false
}

const saveCss = css`
  margin-left: 12px;
`
const deleteButtonCss = css`
  margin-left: -8px;

  @media (${BP_SMALL_UP}) {
    margin-left: -24px;
  }
`

// TODO state should be moved into the wrapper
const PlanItemDetailsModal = ({
  alerts,
  campus,
  disableDelete,
  disableGroupChange,
  isOpen,
  onCancel,
  onDelete,
  onSave,
  planGroups,
  planItem,
  readOnly
}: PlanItemDetailsModalProps) => {
  const isNotMobile = useMedia(`(${BP_SMALL_UP})`)
  const { id, courseNumber, subjectId, locked, required } = planItem
  const isNonCourse = (!courseNumber && !subjectId) || planItem.isCoursePlaceholder
  const currentGroup = planGroups.find((g) => g.id === planItem.groupId)
  const [alertsState, setAlerts] = useState<DetailsPlanAlert[]>(alerts)
  const [selectedCourseItemState, setSelectedCourseItemState] = useState<any>({ ...planItem })
  const [planItemState, setPlanItemState] = useState<PlanItemState>({
    creditsMax:
      typeof planItem.creditsMax === 'number' && planItem.creditsMax >= 0
        ? planItem.creditsMax
        : undefined,
    creditsMin:
      typeof planItem.creditsMin === 'number' && planItem.creditsMin >= 0
        ? planItem.creditsMin
        : undefined,
    description: planItem.description || undefined,
    group: currentGroup,
    inProgress: planItem.inProgress,
    isCoursePlaceholder: planItem.isCoursePlaceholder,
    locked: planItem.locked,
    required: planItem.required,
    nonCourseCreditsMax:
      typeof planItem.nonCourseCreditsMax === 'number' && planItem.nonCourseCreditsMax >= 0
        ? planItem.nonCourseCreditsMax
        : undefined,
    nonCourseCreditsMin:
      typeof planItem.nonCourseCreditsMin === 'number' && planItem.nonCourseCreditsMin >= 0
        ? planItem.nonCourseCreditsMin
        : undefined,
    nonCourseDescription: planItem.nonCourseDescription || '',
    nonCourseTitle: planItem.nonCourseTitle || '',
    requirementTitles: planItem.requirementTitles,
    selectedCourse: planItem.externalCourseId
      ? {
          id: planItem.externalCourseId,
          courseNumber: planItem.courseNumber || '',
          creditsMax: planItem.creditsMax || 0,
          creditsMin: planItem.creditsMin || 0,
          description: planItem.description || '',
          externalCourseId: planItem.externalCourseId || '0',
          requisiteDescription: planItem.requisiteDescription || '',
          requisiteDescriptionLong: planItem.requisiteDescriptionLong || '',
          subjectId: planItem.subjectId || '',
          title: planItem.title || ''
        }
      : undefined,
    selectedCoursePlaceholders:
      (planItem.placeholderCourses as CatalogSearchResultFragment[]) || undefined,
    title: planItem.title,
    usedToMeetRequirement: planItem.usedToMeetRequirement && !planItem.inProgress
  })
  const { user } = useAuth()
  const isStudent = userIsOnlyStudent(user)
  const onChange = (
    name: string,
    value:
      | string
      | boolean
      | number
      | BoardGroupFragment
      | CatalogSearchResultFragment
      | CatalogSearchResultFragment[]
      | undefined
  ) => {
    setPlanItemState({ ...planItemState, [name]: value })
  }

  const onDismissAlert = (alert: DetailsPlanAlert) => {
    // Because alerts are stored in state, need to update state when one is dismissed
    const filteredAlerts = alertsState.filter(
      (alertState) => alert.alertKey !== alertState.alertKey
    )
    setAlerts(filteredAlerts)
  }

  const [placeholderCourseDetailQuery] = usePlaceholderCourseDetailLazyQuery({
    // Must use this fetchPolicy so that the onCompleted method will fire subsequent times that the query is made
    // https://github.com/apollographql/apollo-client/issues/9338
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data && data.placeholderCourseDetail) {
        const newAlerts = data.placeholderCourseDetail.alerts
        let detailAlerts: DetailsPlanAlert[] = []
        const selectedPlaceholderCourse = data.placeholderCourseDetail.course
        if (newAlerts && newAlerts.length) {
          detailAlerts = getPlanItemAlerts(
            newAlerts,
            planItem,
            currentGroup,
            courseLabel,
            advisorLabel,
            termLabel
          )
        }
        setAlerts([...detailAlerts])

        if (selectedPlaceholderCourse) {
          const selectedCourseItem = {
            ...planItem,
            campus: selectedPlaceholderCourse.campus,
            campusDescription: selectedPlaceholderCourse.campusDescription,
            career: selectedPlaceholderCourse.career,
            courseNumber: selectedPlaceholderCourse.courseNumber,
            creditsMax: selectedPlaceholderCourse.creditsMax,
            creditsMin: selectedPlaceholderCourse.creditsMin,
            description: selectedPlaceholderCourse.description,
            externalCourseId: selectedPlaceholderCourse.externalCourseId,
            inProgress: selectedPlaceholderCourse.inProgress,
            requirementTitles: selectedPlaceholderCourse.requirementTitles,
            requisiteDescription: selectedPlaceholderCourse.requisiteDescription,
            requisiteDescriptionLong: selectedPlaceholderCourse.requisiteDescriptionLong,
            subjectId: selectedPlaceholderCourse.subjectId,
            title: selectedPlaceholderCourse.title || '',
            typicallyOffered: selectedPlaceholderCourse.typicallyOffered,
            usedToMeetRequirement:
              selectedPlaceholderCourse.usedToMeetRequirement &&
              !selectedPlaceholderCourse.inProgress
          }
          setSelectedCourseItemState(selectedCourseItem)
        }
      }
    }
  })

  const clearSelectedCourseItem = () => {
    const selectedCourseItem = {
      ...planItem,
      campus: '',
      campusDescription: '',
      career: '',
      courseNumber: '',
      creditsMax: '',
      creditsMin: '',
      description: '',
      externalCourseId: '',
      requisiteDescription: '',
      requisiteDescriptionLong: '',
      subjectId: '',
      title: '',
      typicallyOffered: ''
    }
    setSelectedCourseItemState(selectedCourseItem)
    setAlerts([])
  }

  const onIsCoursePlaceholderChange = () => {
    // Multiple state updates don't play nicely, clear everything
    // related to placeholders at once if this is no longer a placeholder
    if (planItemState.isCoursePlaceholder) {
      clearSelectedCourseItem()
      setPlanItemState({
        ...planItemState,
        isCoursePlaceholder: false,
        selectedCourse: undefined,
        selectedCoursePlaceholders: undefined
      })
    } else {
      onChange('isCoursePlaceholder', true)
    }
  }

  const onPlaceholderCourseSelected = (selectedCourse: CatalogSearchResultFragment | undefined) => {
    if (selectedCourse) {
      placeholderCourseDetailQuery({
        variables: {
          courseNumber: selectedCourse.courseNumber,
          externalCourseId: selectedCourse.externalCourseId,
          itemId: id,
          subjectId: selectedCourse.subjectId
        },
        context: { useApolloNetworkStatus: false }
      })
    } else {
      // If the course was cleared out
      clearSelectedCourseItem()
    }
  }

  const {
    isCoursePlaceholder,
    nonCourseCreditsMin,
    nonCourseCreditsMax,
    nonCourseDescription,
    group,
    nonCourseTitle
  } = planItemState
  const { sectionFieldText, text, academicPlanningSettings } = useAppSettings()
  const advisorLabel = text.advisor
  const creditsLabel = sectionFieldText.credits
  const courseLabel = text.course
  const termLabel = text.term
  const campusLabel = text.campus
  const { courseDisplayFormat } = academicPlanningSettings

  // If non course and not a placeholder = Edit Item Details
  // If non course and is a placeholder
  //  And course isn't selected = Select Course
  //  And course is selected = Edit Placeholder Selection: subject id courseNumber - title
  // Regular course = subjectId courseNumber - title
  let modalTitle
  if (isNonCourse) {
    if (isCoursePlaceholder) {
      modalTitle = 'Select Course'
    } else {
      modalTitle = 'Edit Item Details'
    }
  } else if (isCoursePlaceholder) {
    modalTitle = `Edit Placeholder Selection: ${stringFormat(courseDisplayFormat, planItem)}`
  } else {
    modalTitle = stringFormat(courseDisplayFormat, planItem)
  }
  const deleteButton = (
    <SecondaryButton
      css={deleteButtonCss}
      disabled={readOnly || (isStudent && (locked || required)) || disableDelete}
      onClick={onDelete}
    >
      Delete Item
    </SecondaryButton>
  )

  // Determine if the requisite label should display as error text
  const requisiteAlert = alertsState.find((alert) => alert.alertType === AlertType.Requisite)
  const hasRequisiteError =
    (selectedCourseItemState.requisiteDescription ||
      selectedCourseItemState.requisiteDescriptionLong) &&
    requisiteAlert

  const formBody =
    isNonCourse || isCoursePlaceholder ? (
      <NonCourseForm
        advisorLabel={advisorLabel}
        alerts={alertsState}
        campus={campus}
        campusLabel={campusLabel}
        courseDisplayFormat={courseDisplayFormat}
        courseLabel={courseLabel}
        creditsLabel={creditsLabel}
        creditsMax={nonCourseCreditsMax}
        creditsMin={nonCourseCreditsMin}
        description={nonCourseDescription}
        groupId={group?.id}
        hasRequisiteError={hasRequisiteError}
        isCoursePlaceholder={planItem.isCoursePlaceholder}
        isCoursePlaceholderState={planItemState.isCoursePlaceholder}
        isStudent={isStudent}
        locked={planItemState.locked}
        onChange={onChange}
        onIsCoursePlaceholderChange={onIsCoursePlaceholderChange}
        onDelete={onDelete}
        onDismissAlert={onDismissAlert}
        onPlaceholderCourseSelected={onPlaceholderCourseSelected}
        planGroups={planGroups}
        planItem={selectedCourseItemState}
        readOnly={readOnly}
        required={planItemState.required}
        selectedCourse={planItemState.selectedCourse}
        selectedCoursePlaceholders={planItemState.selectedCoursePlaceholders}
        termLabel={termLabel}
        title={nonCourseTitle}
      />
    ) : (
      <CourseForm
        alerts={alerts}
        campusLabel={campusLabel}
        courseLabel={courseLabel}
        creditsLabel={creditsLabel}
        disableGroupChange={disableGroupChange}
        groupId={group?.id}
        hasRequisiteError={hasRequisiteError}
        isStudent={isStudent}
        locked={planItemState.locked}
        onChange={onChange}
        onDelete={onDelete}
        planGroups={planGroups}
        planItem={planItem}
        readOnly={readOnly}
        required={planItemState.required}
        termLabel={termLabel}
      />
    )

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(planItemState)
  }

  return (
    <Modal allowMobilePage isOpen={isOpen} onSubmit={handleSave} size={ModalSize.LG}>
      <ModalHeader allowMobile onClose={onCancel} title={modalTitle} />
      <ModalBody allowMobile>{formBody}</ModalBody>
      <ModalFooter startElement={deleteButton}>
        {isNotMobile && <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>}
        <PrimaryButton
          css={saveCss}
          disabled={readOnly}
          type={ButtonType.Submit}
          onClick={handleSave}
          small
        >
          Save
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  )
}

PlanItemDetailsModal.defaultProps = defaultProps

export default PlanItemDetailsModal
